import * as THREE from 'three';
import CruciblePiece from './cruciblePiece';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { ShellConfig } from '../config';
import * as config from '../config.json';
import CrucibleLoader from '../crucibleLoader';
import getCrucibleStickers from './extras/getCrucibleStickers';

export class Shell extends CruciblePiece {
  protected _mixer: THREE.AnimationMixer;
  protected _openingAnimation: THREE.AnimationClip;
  protected _config: ShellConfig;
  protected _baseShell: GLTF | null;

  protected _textureLoader: THREE.TextureLoader;

  protected _innerRingTextures: THREE.Texture[];
  protected _outerRingTextures: THREE.Texture[];
  protected _ornamentRingTextures: THREE.Texture[];
  protected _glassTextures: THREE.Texture[];
  protected _cutShapeTextures: THREE.Texture[];

  protected _starNode: THREE.Object3D;
  protected _ornamentRingNode: THREE.Object3D;
  protected _innerRingNode: THREE.Object3D;
  // protected _outerRingNode: THREE.Object3D;

  constructor() {
    super();
    this._config = config.shell;
    this._textureLoader = new THREE.TextureLoader();

    this._mixer = new THREE.AnimationMixer(this.group);
    this._openingAnimation = new THREE.AnimationClip(
      this._config.openingAnimationName,
      0,
      []
    );

    this._baseShell = null;

    this._innerRingTextures = [];
    this._outerRingTextures = [];
    this._ornamentRingTextures = [];
    this._glassTextures = [];
    this._cutShapeTextures = [];

    this._starNode = new THREE.Object3D();
    this._ornamentRingNode = new THREE.Object3D();
    this._innerRingNode = new THREE.Object3D();
  }

  async loadAllComponents(
    loader: CrucibleLoader,
    innerRingUrl: string,
    outerRingUrl: string,
    ornamentRingUrl: string,
    glassUrl: string,
    cutShapeUrl: string
  ) {
    try {
      // Clean up local arrays in case there was a previous crucible loaded
      this._innerRingTextures = [];
      this._outerRingTextures = [];
      this._ornamentRingTextures = [];
      this._glassTextures = [];
      this._cutShapeTextures = [];

      // TODO: change this so it doesn't wait for each component textures to load to start the next one
      // Load outer ring first and check if the others are the same textures before loading them
      this._outerRingTextures = await this.loadComponentTextures(
        loader,
        outerRingUrl
      );

      if (innerRingUrl === outerRingUrl) {
        this._innerRingTextures = this._outerRingTextures;
      } else {
        this._innerRingTextures = await this.loadComponentTextures(
          loader,
          innerRingUrl
        );
      }

      if (ornamentRingUrl === outerRingUrl) {
        this._ornamentRingTextures = this._outerRingTextures;
      } else if (ornamentRingUrl === innerRingUrl) {
        this._ornamentRingTextures = this._innerRingTextures;
      }

      if (
        ornamentRingUrl !== innerRingUrl &&
        ornamentRingUrl !== outerRingUrl
      ) {
        this._ornamentRingTextures = await this.loadComponentTextures(
          loader,
          ornamentRingUrl
        );
      }

      this._glassTextures = await this.loadComponentTextures(
        loader,
        glassUrl,
        true,
        true
      );

      this._cutShapeTextures = await this.loadComponentTextures(
        loader,
        cutShapeUrl,
        true,
        false
      );

      // After all textures are loaded, add them to the materials
      this.group.traverse((node) => {
        if (node instanceof THREE.Mesh) {
          if (node.material.name === this._config.outerRingMaterial) {
            node.material.map = this._outerRingTextures[0];
            node.material.roughnessMap = this._outerRingTextures[1];
            node.material.metalnessMap = this._outerRingTextures[1];
            node.material.aoMap = this._outerRingTextures[1];
            node.material.normalMap = this._outerRingTextures[2];
          }

          if (node.material.name === this._config.innerRingMaterial) {
            node.material.map = this._innerRingTextures[0];
            node.material.roughnessMap = this._innerRingTextures[1];
            node.material.metalnessMap = this._innerRingTextures[1];
            node.material.aoMap = this._innerRingTextures[1];
            node.material.normalMap = this._innerRingTextures[2];
          }

          if (node.material.name === this._config.ornamentRingMaterial) {
            node.material.map = this._ornamentRingTextures[0];
            node.material.roughnessMap = this._ornamentRingTextures[1];
            node.material.metalnessMap = this._ornamentRingTextures[1];
            node.material.aoMap = this._ornamentRingTextures[1];
            node.material.normalMap = this._ornamentRingTextures[2];
          }

          if (node.material.name === this._config.cutShapeMaterial) {
            this._cutShapeTextures.forEach((texture) => {
              texture.magFilter = THREE.LinearFilter;
              texture.minFilter = THREE.LinearFilter;
            });

            node.material.map = this._cutShapeTextures[0];
            node.material.roughnessMap = this._cutShapeTextures[1];
            node.material.metalnessMap = this._cutShapeTextures[1];
            node.material.aoMap = this._cutShapeTextures[1];
            node.material.normalMap = this._cutShapeTextures[2];

            node.material.transparent = true;
            node.material.needsUpdate = true;
          }

          if (node.material.name === this._config.glassMaterial) {
            node.material.map = this._glassTextures[0];
            node.material.roughnessMap = this._glassTextures[1];
            node.material.metalnessMap = this._glassTextures[1];
            node.material.aoMap = this._glassTextures[1];
            node.material.normalMap = this._glassTextures[2];
            node.material.emissiveMap = this._glassTextures[3];
          }

          node.castShadow = true;
          node.receiveShadow = true;
          if (node.material.map) node.material.map.anisotropy = 16;
        }
      });
    } catch (error) {
      throw new Error('Could not load Shell textures');
    }
  }

  async loadComponentTextures(
    loader: CrucibleLoader,
    texturesBaseUrl: string,
    transparency: boolean = false,
    emissive: boolean = false
  ) {
    const texturePromises: Promise<THREE.Texture | undefined>[] = [];

    let textureFormat = loader.quality.textureFormat;
    if (transparency) textureFormat = loader.quality.transparencyFormat;

    texturePromises[0] = loader.loadTexture(
      `${texturesBaseUrl}_${this._config.textureNameAlbedo}.${textureFormat}`,
      transparency
    );

    texturePromises[1] = loader.loadTexture(
      `${texturesBaseUrl}_${this._config.textureNameMetallic}.${textureFormat}`,
      transparency
    );

    texturePromises[2] = loader.loadTexture(
      `${texturesBaseUrl}_${this._config.textureNameNormal}.${textureFormat}`,
      transparency
    );

    if (emissive) {
      texturePromises[3] = loader.loadTexture(
        `${texturesBaseUrl}_${this._config.textureEmissive}.${textureFormat}`,
        transparency
      );
    }

    const textureResults = await Promise.all(texturePromises);

    let textures: THREE.Texture[] = [];

    textureResults.forEach((texture) => {
      if (texture !== undefined) {
        texture.flipY = false;
        texture.encoding = THREE.sRGBEncoding;
        textures.push(texture);
      }
    });

    return textures;
  }

  setup(gltf: GLTF) {
    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2);
    this.group.traverse((node: any) => {
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;
      }

      if (node.name === this._config.starNodeName) {
        this._starNode = node;
      }
      if (node.name === this._config.ornamentNodeName) {
        this._ornamentRingNode = node;
      }
      if (node.name === this._config.innerRingNodeName) {
        this._innerRingNode = node;
      }
    });

    gltf.animations.forEach((clip: THREE.AnimationClip) => {
      if (clip.name === this._config.openingAnimationName) {
        this._openingAnimation = clip;
        this._mixer = new THREE.AnimationMixer(this.group);
      }
    });
  }

  playKeyFrameAnimations(timeScale: number = 1) {
    const openingAnimationAction = this._mixer.clipAction(
      this._openingAnimation
    );

    openingAnimationAction.paused = false;
    openingAnimationAction.timeScale = timeScale;
    openingAnimationAction.setLoop(THREE.LoopOnce, 1);
    openingAnimationAction.clampWhenFinished = true;

    openingAnimationAction.play();
  }

  playKeyFrameAnimationsReversed(timeScale: number = 1) {
    const openingAnimationAction = this._mixer.clipAction(
      this._openingAnimation
    );

    openingAnimationAction.paused = false;
    openingAnimationAction.timeScale = -1 * timeScale;
    openingAnimationAction.setLoop(THREE.LoopOnce, 1);
    openingAnimationAction.clampWhenFinished = true;

    openingAnimationAction.play();
  }

  animate(timeElapsed: number, screenshotMode?: boolean) {
    this._mixer.update(timeElapsed);

    if (!screenshotMode) {
      this._starNode.rotation.y += this._config.starRotationSpeed * timeElapsed;
      this._ornamentRingNode.rotation.y -=
        this._config.ornamentRotationSpeed * timeElapsed;
      this._innerRingNode.rotation.y +=
        this._config.innerRingRotationSpeed * timeElapsed;
    }
  }

  async update(mintNumber: number, loader: CrucibleLoader) {
    // Add the Alien sticker for the first 1000 crucibles
    const stickers: THREE.Object3D[] = getCrucibleStickers(mintNumber, loader);
    stickers.forEach((sticker) => {
      this.group.add(sticker);
    });
  }
}
